import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, map, switchMap, of, tap, filter } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BpService } from '../../../core/services/bp.service';
import { BpFromResolve, PreviousPage } from '../../../core/models/bp.model';
import { RetailPlaceService } from '../../../core/services/retail-place.service';
import {
  AddressField,
  AddressFormModel,
  RetailPlaceAddress,
  RetailPlaceFields,
  RetailPlaceForm,
  SelectedItemModel,
} from './new-retail-place.model';
import { KkmsPageState } from '../kkms-page/kkms-page.state';
import { ModalService } from '../../../core/modal/modal.service';
import { ManualInputAddressComponent } from './manual-input-address/manual-input-address.component';
import { BusinessProcessApiService } from '../../../core/modules/business-process/business-process-api.service';
import { RegionItemModel, StreetItemModel } from '../../../core/modules/retail-places/retail-paces.types';
import { BrowserStorageService } from '../../../core/services/browser-storage.service';
import { SettingsService } from '../../../core/services/settings.service';
import { DEFAULT_RETAIL_PLACE_ADDRESS } from './new-retail-place.config';

@UntilDestroy()
@Component({
  selector: 'tax-new-retail-place',
  templateUrl: './new-retail-place.component.html',
  styleUrls: ['./new-retail-place.component.sass'],
})

export class NewRetailPlaceComponent implements OnInit, OnDestroy {
  retailPlace: RetailPlaceFields = {
    retailPlaceTitle: '',
    retailPlaceAddress: DEFAULT_RETAIL_PLACE_ADDRESS,
  };

  isEditForm = false;
  manualInputStreet = false;
  manualInputHouse = false;
  isIsnaEnabled = false;
  isRcaNormalizationProcess = false;
  readonly alertMessage = 'rca.alarm.retail-place.content';
  private regionList: RegionItemModel[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bpService: BpService,
    private bpApiService: BusinessProcessApiService,
    private location: Location,
    private retailPlaceService: RetailPlaceService,
    private retailPlacesPageState: KkmsPageState,
    private modalService: ModalService,
    private browserStorageService: BrowserStorageService,
    private settingsService: SettingsService,
  ) {}

  ngOnInit(): void {
    this.saveHistoryState();
    this.initRetailPlaceForm();
  }

  ngOnDestroy(): void {
    this.browserStorageService.remove(['state']);
  }

  getRegion(region: string): Observable<SelectedItemModel[]> {
    return this.retailPlaceService.getRegion(region).pipe(
      map(
        (res) => {
          this.regionList = res;
          return res.map(item => ({
            title: item.title, id: String(item.localityId),
          }));
        },
      ),
    );
  }

  setRegion(item: SelectedItemModel): void {
    if (this.retailPlace.retailPlaceAddress.region === item.title) return;
    this.updateFormValues('region');
    this.retailPlace.retailPlaceAddress.region = item.title;
    this.retailPlace.retailPlaceAddress.regionId = item.id;
    this.retailPlace.retailPlaceAddress.regionCode = this.regionList
      .find(element => item.id === String(element.localityId))?.code ?? null;
    this.resetManualInput();
  }

  getCity(city: string): Observable<SelectedItemModel[]> {
    if (!this.retailPlace.retailPlaceAddress.regionId) return of([]);
    return this.retailPlaceService.getToponyms(this.retailPlace.retailPlaceAddress.regionId, city).pipe(
      map(
        res => res.map((item) => {
          const child = item.children.map(childItem => ({
            title: childItem.path, id: childItem.localityId,
          }));
          return {
            title: item.path, id: item.localityId, child,
          };
        }),
      ),
    );
  }

  setCity(item: SelectedItemModel): void {
    if (this.retailPlace.retailPlaceAddress.city === item.title) return;
    this.updateFormValues('city');
    this.retailPlace.retailPlaceAddress.city = item.title;
    this.retailPlace.retailPlaceAddress.localityId = item.id;
    this.resetManualInput();
  }

  getStreet(street: string): Observable<SelectedItemModel[]> {
    if (!this.retailPlace.retailPlaceAddress.localityId) return of([]);
    return this.retailPlaceService.getGeonims(this.retailPlace.retailPlaceAddress.localityId, street).pipe(
      map(
        result => result.map((item) => {
          const streetItem = {
            ...item,
          };
          delete streetItem.children;
          if (item.children && item.children.length > 0) {
            item.children.unshift(streetItem);
          }

          const child = item.children?.map(childItem => ({
            title: childItem.title, id: String(childItem.id),
          }));
          return {
            id: String(item.id), title: item.title, child,
          };
        }),
      ),
    );
  }

  setStreet(item?: SelectedItemModel): void {
    if (this.retailPlace.retailPlaceAddress.street === item?.title) return;
    this.updateFormValues('street');
    this.retailPlace.retailPlaceAddress.street = item?.title ?? null;
    this.retailPlace.retailPlaceAddress.geonimId = item?.id ?? null;
    this.manualInputHouse = false;
    if (!item) this.manualInputStreet = false;
  }

  getHouse(building: string): Observable<SelectedItemModel[]> {
    if (!this.retailPlace.retailPlaceAddress.geonimId) return of([]);
    return this.retailPlaceService.getBuildings(this.retailPlace.retailPlaceAddress.geonimId, building).pipe(
      map(res => res.map(el => ({
        id: String(el.id), title: el.title, rca: el.rca,
      }))),
    );
  }

  setHouse(item?: SelectedItemModel): void {
    if (this.retailPlace.retailPlaceAddress.house === item?.title) return;
    this.updateFormValues('house');
    this.retailPlace.retailPlaceAddress.house = item?.title ?? null;
    this.retailPlace.retailPlaceAddress.buildingId = item?.id ?? null;
    this.retailPlace.retailPlaceAddress.rca = item?.rca ?? null;
    if (!item) {
      this.manualInputHouse = false;
    }
  }

  onSubmit(): void {
    if (!this.formValidation()) return;
    /* при сабмите в случае когда мы редактируем форму отправляется старый адресс в поле `fullAddress`, чтобы этого предотвратить
    отправляем null, так как сам полный адресс формируется на бэке */
    if (this.isEditForm) this.retailPlace.retailPlaceAddress.fullAddress = null;
    const { pid, tid } = this.bpService.getProcessIds();
    this.bpService.submit(pid, tid, this.retailPlace).pipe(untilDestroyed(this)).subscribe(() => {
      if (!this.isEditForm) this.retailPlacesPageState.resetAllStates();
      this.navigateHandler(true);
    });
  }

  goBack(): void {
    this.navigateHandler(false);
  }

  formValidation(): boolean {
    const rp = this.retailPlace.retailPlaceAddress;
    const regExp = this.retailPlace.retailPlaceRegex;
    const isApartmentValid = rp.apartment
      ? new RegExp(`^${regExp?.apartment ?? ''}$`).test(rp.apartment)
      : true;
    return (
      Boolean(this.retailPlace.retailPlaceTitle && rp.region && rp.city && rp.street && rp.house)
      && isApartmentValid
    );
  }

  openManualInputAddressModal(street: string = ''): void {
    this.modalService.openOld<AddressFormModel>(ManualInputAddressComponent, {
      inputStreet: street, regExp: this.retailPlace.retailPlaceRegex,
    }, {
      width: '600px',
    })
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.retailPlace.retailPlaceAddress = {
          // Для сброса значения rca в null необходимо на бэкенд отправлять значение "0"
          ...this.retailPlace.retailPlaceAddress, ...data, rca: '0',
        };
        this.manualInputHouse = true;
        this.retailPlace.retailPlaceAddress.buildingId = null;
        if (!street) {
          this.manualInputStreet = true;
          this.retailPlace.retailPlaceAddress.geonimId = null;
        }
      });
  }

  private initRetailPlaceForm(): void {
    this.isEditForm = !!this.route.snapshot.paramMap.get('retailPlaceId');

    (this.route.data as Observable<BpFromResolve>)
      .pipe(
        switchMap(data => this.bpService.saveProcessIds(data.bp)),
        switchMap(() => {
          const { pid, tid } = this.bpService.getProcessIds();
          return this.bpApiService.getFormData<RetailPlaceForm>(pid, tid);
        }),
        tap((processForm) => {
          this.settingsService.getSettings().pipe(untilDestroyed(this))
            .subscribe((settings) => {
              this.isIsnaEnabled = settings['kkm.registration.isna.enabled'] === 'true';
              this.isRcaNormalizationProcess = this.isIsnaEnabled
                && this.isEditForm
                && !processForm.fields.retailPlaceAddress.rca;
              const retailPlaceAddress = this.isRcaNormalizationProcess
                ? DEFAULT_RETAIL_PLACE_ADDRESS
                : processForm.fields.retailPlaceAddress;
              this.retailPlace = {
                ...processForm.fields, retailPlaceAddress,
              };
            });
        }),
        filter(processForm => !processForm.fields.retailPlaceAddress.regionId && this.isEditForm),
        switchMap(processForm => this.retailPlaceService.getRegion(processForm.fields.retailPlaceAddress.region ?? '')),
        map(regions => regions.find(region => region.title === this.retailPlace.retailPlaceAddress.region)?.localityId),
        filter(Boolean),
        tap((regionId) => {
          this.retailPlace = {
            ...this.retailPlace,
            retailPlaceAddress: {
              ...this.retailPlace.retailPlaceAddress,
              regionId: String(regionId),
            },
          };
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private updateFormValues(value: AddressField): void {
    this.retailPlace = {
      ...this.retailPlace,
      retailPlaceAddress: {
        ...this.retailPlace.retailPlaceAddress,
        city: value === 'region' ? '' : this.retailPlace.retailPlaceAddress.city,
        localityId: value === 'region' ? '' : this.retailPlace.retailPlaceAddress.localityId,
        street: ['region', 'city'].includes(value) ? '' : this.retailPlace.retailPlaceAddress.street,
        geonimId: ['region', 'city'].includes(value) ? '' : this.retailPlace.retailPlaceAddress.geonimId,
        house: ['region', 'city', 'street'].includes(value) ? '' : this.retailPlace.retailPlaceAddress.house,
        buildingId: ['region', 'city', 'street'].includes(value) ? '' : this.retailPlace.retailPlaceAddress.buildingId,
        apartment: ['region', 'city', 'street', 'house'].includes(value)
          ? '' : this.retailPlace.retailPlaceAddress.apartment,
      },
    };
  }

  private resetManualInput(): void {
    this.manualInputHouse = false;
    this.manualInputStreet = false;
  }

  private navigateHandler(isUpdatedRca: boolean): void {
    const state = this.browserStorageService.get<PreviousPage>('state');
    if (state?.state && state?.previousPageUrl) {
      this.router.navigateByUrl(state.previousPageUrl, {
        state: {
          state: state.state,
          isUpdatedRca,
        },
        replaceUrl: true,
      });
    } else this.location.back();
  }

  private saveHistoryState(): void {
    const { state, previousPageUrl } = history?.state as PreviousPage ?? {};

    if (state && previousPageUrl) {
      this.browserStorageService.set('state', {
        state, previousPageUrl,
      });
    }
  }
}
